import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import TickerTimeout from "../../../utils/TickerTimeout";
import { GAME_CONTAINER_OFFSET_CONFIG } from "../../../Games/Main/GameContainer/GameContainer";

// Match exact background offset so tree/flowers align pixel-perfectly with background textures
// This is the same value used in BackgroundAnimationController
const BACKGROUND_Y_OFFSET = -158;

/**
 * BackgroundDecorationController
 *
 * Renders the `front_mask` animation from the background_animation spine.
 * This is a dedicated overlay layer (tree, flowers) that sits inside UserInterface
 * with zIndex 7 (above GameContainer at z5, below toolbar buttons at z10).
 *
 * Uses the exact same positioning math as BackgroundAnimationController to guarantee
 * pixel-perfect alignment with the background textures.
 */
class BackgroundDecorationController {
  constructor(container, dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.decorationSpine = null;

    this.initialize();
  }

  initialize() {
    try {
      const assets = PIXI.Assets.cache;
      const hasSkeleton = assets.has("background_animation-skeleton");
      const hasAtlas = assets.has("background_animation-atlas");

      if (!hasSkeleton || !hasAtlas) {
        // Assets not loaded yet — retry after a short delay
        if (!this._retryCount) this._retryCount = 0;
        if (this._retryCount < 20) {
          this._retryCount++;
          this._retryTimeout = new TickerTimeout(() => this.initialize(), 500);
        }
        return;
      }

      this.decorationSpine = Spine.from({
        skeleton: "background_animation-skeleton",
        atlas: "background_animation-atlas",
      });

      if (!this.decorationSpine?.skeleton || !this.decorationSpine?.state) {
        this.decorationSpine = null;
        return;
      }

      this.decorationSpine.visible = true;
      this.container.addChild(this.decorationSpine);

      // Play front_mask looping — always visible
      const anims = this.decorationSpine.skeleton.data.animations.map(
        (a) => a.name,
      );
      if (anims.includes("front_mask")) {
        this.decorationSpine.state.setAnimation(0, "front_mask", true);
        // Advance one frame to populate skeleton transform
        this.decorationSpine.state.update(0.016);
        this.decorationSpine.skeleton.updateWorldTransform();
      }

      // Apply responsive positioning (same math as BackgroundAnimationController)
      this.applyResponsiveScaling();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Apply responsive scaling and positioning — mirrors BackgroundAnimationController exactly.
   * Both layers use the same BACKGROUND_Y_OFFSET, GAME_CONTAINER_OFFSET_CONFIG,
   * and symbolScale to guarantee pixel-perfect alignment.
   */
  applyResponsiveScaling() {
    if (!this.decorationSpine || !this.dimensions) return;

    const { width, height } = this.dimensions;

    // Only scale when symbols start shrinking (scaleFactor < 1) — same as background
    const backgroundScale = Math.min(1, this.symbolScale);

    const centerX = width / 2;

    // Follow GameContainer offset (so decoration moves with reels + background)
    const deviceConfig = GAME_CONTAINER_OFFSET_CONFIG[this.device];
    const offsetMultiplier =
      typeof deviceConfig === "number"
        ? deviceConfig
        : (deviceConfig?.[this.orientation] ?? 0);
    const gameContainerOffset = this.symbolHeight * offsetMultiplier;

    // Stone ledge offset — scale proportionally with background
    const scaledOffset = BACKGROUND_Y_OFFSET * backgroundScale;
    const positionY = height / 2 - gameContainerOffset + scaledOffset;

    this.decorationSpine.scale.set(backgroundScale);
    this.decorationSpine.position.set(centerX, positionY);
  }

  /**
   * Update dimensions and reapply scaling (called on resize)
   */
  resize(dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation) {
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.applyResponsiveScaling();
  }

  cleanup() {
    if (this._retryTimeout) {
      if (this._retryTimeout && typeof this._retryTimeout.clear === "function") this._retryTimeout.clear();
      this._retryTimeout = null;
    }

    if (this.decorationSpine) {
      this.decorationSpine.state?.clearListeners();
      this.decorationSpine.state?.clearTracks();
      if (
        this.container &&
        this.container.children.includes(this.decorationSpine)
      ) {
        this.container.removeChild(this.decorationSpine);
      }
      this.decorationSpine.destroy();
      this.decorationSpine = null;
    }
  }
}

export default BackgroundDecorationController;
